/* eslint-disable max-lines */
/* eslint-disable import/max-dependencies */

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import AssistantIcon from '@mui/icons-material/Assistant';
import {
  useSageSsePost,
  useVenderProgramsDownloadFile,
} from './hooks/apiHooks';
import Snackbar from '@mui/material/Snackbar';

import Markdown from 'react-markdown';
import dompurify from 'dompurify';

import { useTranslate } from '@im/react-localization';
import remarkBreaks from 'remark-breaks';
import { downloadBlob } from 'src/util/fileUtil';
import SageAiDisclaimer from './disclaimer';

type SageMessage = {
  isApiResponse: boolean;
  sender: 'bot' | 'user';
  text: string;
};

type DownloadLinkData = {
  fileName: string;
  filePath: string;
  vendorName: string;
};

type SseEventHandlers = {
  updateFinalResponse: (text: string, replace?: boolean) => void;
  updateThinkingMessage: (text: string) => void;
};

type DownloadMessage = {
  isApiResponse: boolean;
  text?: string;
};

type FileIdResponse = {
  response?: unknown;
};

type SseResult = {
  artifact?: {
    parts?: Array<{ text?: string }>;
  };
  kind?: string;
  status?: {
    message?: {
      parts?: Array<{ text?: string }>;
    };
  };
};

type SseEventData = {
  agent?: string;
  details?: string;
  message?: string;
  result?: SseResult;
  text?: string;
  tool?: string;
  type?: string;
};

type UpdateStreamingBubble = (text: string, isApiResponse?: boolean) => void;

const DOWNLOAD_FILE_REGEX = /\/download-file\/([^\s)\]]+)/;
const VENDOR_PROGRAMS_REGEX = /^([\w-]+)_Vendor_Programs/i;

export function formatToolCallMessage(
  agent?: string,
  tool?: string,
  details?: string
) {
  return `${agent || 'agent'} • ${tool || 'tool'}: ${details || '...'}`;
}

export function formatToolResultMessage(
  tool?: string,
  details?: string,
  message?: string
) {
  return `${tool || 'tool'}: ${details || message || 'completed'}`;
}

export function handleStatusUpdateText(
  textContent: string,
  updateThinkingMessage: (text: string) => void
) {
  try {
    const msgContent = JSON.parse(textContent);
    if (msgContent.type === 'agent_transfer') {
      updateThinkingMessage(msgContent.message || 'Agent transfer in progress');
      return;
    }
    if (msgContent.type === 'tool_call') {
      updateThinkingMessage(
        formatToolCallMessage(
          msgContent.agent,
          msgContent.tool,
          msgContent.details
        )
      );
      return;
    }
    if (msgContent.type === 'tool_result') {
      updateThinkingMessage(
        formatToolResultMessage(
          msgContent.tool,
          msgContent.details,
          msgContent.message
        )
      );
    }
  } catch {
    updateThinkingMessage(String(textContent).substring(0, 150));
  }
}

function handleSseResult(result: SseResult, handlers: SseEventHandlers) {
  const { updateFinalResponse, updateThinkingMessage } = handlers;

  if (result.kind === 'artifact-update' && result.artifact?.parts?.length) {
    const artifactText = result.artifact.parts[0]?.text;
    if (artifactText) {
      updateFinalResponse(String(artifactText), true);
    }
  }

  if (
    result.kind !== 'status-update' ||
    !result.status?.message?.parts?.length
  ) {
    return;
  }

  const textContent = result.status.message.parts[0]?.text;
  if (!textContent) {
    return;
  }

  handleStatusUpdateText(textContent, updateThinkingMessage);
}

function handleSseEventType(
  eventData: SseEventData,
  handlers: SseEventHandlers
) {
  const { updateFinalResponse, updateThinkingMessage } = handlers;

  switch (eventData.type) {
    case 'agent_transfer':
      updateThinkingMessage(
        eventData.message || eventData.text || 'Agent transfer in progress'
      );
      break;
    case 'tool_call':
      updateThinkingMessage(
        formatToolCallMessage(
          eventData.agent,
          eventData.tool,
          eventData.details
        )
      );
      break;
    case 'tool_result':
      updateThinkingMessage(
        formatToolResultMessage(
          eventData.tool,
          eventData.details,
          eventData.message
        )
      );
      break;
    case 'artifact':
      if (eventData.text) {
        updateFinalResponse(String(eventData.text), true);
      }
      break;
    case 'token':
    case 'chunk':
      if (eventData.text) {
        updateFinalResponse(String(eventData.text));
      }
      break;
    default:
      break;
  }
}

export function createStreamingBubbleUpdater(
  setMessages: React.Dispatch<React.SetStateAction<SageMessage[]>>
): UpdateStreamingBubble {
  return (text: string, isApiResponse = false) => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const last = next.at(-1);
      if (last?.sender !== 'bot') {
        next.push({ isApiResponse, sender: 'bot', text });
        return next;
      }
      next.splice(-1, 1, { ...last, isApiResponse, text });
      return next;
    });
  };
}

export function createFinalResponseUpdater(
  updateStreamingBubble: UpdateStreamingBubble
) {
  let streamedResponse = '';

  return {
    getStreamedResponse: () => streamedResponse,
    updateFinalResponse: (text: string, replace = false) => {
      streamedResponse = replace ? text : `${streamedResponse}${text}`;
      if (streamedResponse.trim()) {
        updateStreamingBubble(streamedResponse, true);
      }
    },
  };
}

export function processSseBuffer(
  buffer: string,
  handlers: SseEventHandlers
): { remaining: string } {
  const lines = buffer.split('\n');
  const remaining = lines.at(-1) ?? '';

  for (let i = 0; i < lines.length - 1; i += 1) {
    const line = lines[i]?.trim();
    if (!line || line.startsWith(':')) continue;
    const parsed = parseJsonLine(line);
    if (parsed) {
      handleSseEvent(parsed, handlers);
    }
  }

  return { remaining };
}

async function consumeSseStream(
  body: ReadableStream<Uint8Array>,
  handlers: SseEventHandlers
) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  let readResult = await reader.read();
  while (!readResult.done) {
    const { value } = readResult;
    if (!value) {
      readResult = await reader.read();
      continue;
    }

    buffer += decoder.decode(value, { stream: true });
    const { remaining } = processSseBuffer(buffer, handlers);
    buffer = remaining;
    readResult = await reader.read();
  }
}

export function extractVendorName(fileName: string): string {
  const vendorMatch = VENDOR_PROGRAMS_REGEX.exec(fileName);
  return vendorMatch?.[1] ?? '';
}

export function extractFileId(
  responseObj: FileIdResponse | null | undefined
): string | null {
  if (!responseObj || typeof responseObj.response !== 'string') {
    return null;
  }
  const text = responseObj.response;
  const match = DOWNLOAD_FILE_REGEX.exec(text);

  if (!match) {
    return null;
  }

  return `download-file/${match[1]}`;
}

export function getDownloadLinkData(
  msg: DownloadMessage
): DownloadLinkData | null {
  if (!(typeof msg.text === 'string' && msg.text.includes('/download-file/')))
    return null;

  const start = msg.text.indexOf('/download-file/');
  if (start === -1) return null;

  const xlsxEnd = msg.text.indexOf('.xlsx', start);
  if (xlsxEnd === -1) return null;

  const filePath = msg.text.substring(start + 1, xlsxEnd + 5).trim();
  const fileName = filePath.substring(filePath.lastIndexOf('/') + 1);
  if (!filePath || !fileName) return null;

  return {
    fileName,
    filePath,
    vendorName: extractVendorName(fileName),
  };
}

export function parseJsonLine(line: string) {
  let payload = '';
  if (line.startsWith('data: ')) {
    payload = line.slice(6);
  } else if (line.startsWith('message\t')) {
    payload = line.slice(8);
  }
  if (!payload) return null;
  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

export function handleSseEvent(
  eventData: SseEventData,
  handlers: SseEventHandlers
) {
  if (!eventData) return;
  handleSseEventType(eventData, handlers);
  if (eventData.result) {
    handleSseResult(eventData.result, handlers);
  }
}

export function toSafeMarkdownText(text: unknown): string {
  if (typeof text !== 'string') {
    return '';
  }

  return dompurify.sanitize(text);
}

export default function SageAi() {
  const location = useLocation();
  const translate = useTranslate();
  const [messages, setMessages] = useState<SageMessage[]>([
    {
      isApiResponse: false,
      sender: 'bot',
      text: 'Hello! How can I help you today?',
    },
  ]);

  // State for draggable button position (starts at bottom-right)
  const [buttonPos, setButtonPos] = useState(() => ({
    x: typeof window !== 'undefined' ? window.innerWidth - 100 : 0,
    y: typeof window !== 'undefined' ? window.innerHeight - 100 : 0,
  }));
  const dragRef = useRef({
    moved: false,
    startPosX: 0,
    startPosY: 0,
    startX: 0,
    startY: 0,
  });

  const handleFabMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    dragRef.current = {
      moved: false,
      startPosX: buttonPos.x,
      startPosY: buttonPos.y,
      startX: e.clientX,
      startY: e.clientY,
    };

    const handleMove = (moveEvent: MouseEvent) => {
      const { startPosX, startPosY, startX, startY } = dragRef.current;
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      if (Math.hypot(deltaX, deltaY) > 5) {
        dragRef.current.moved = true;
      }

      setButtonPos({
        x: startPosX + deltaX,
        y: startPosY + deltaY,
      });
    };

    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  };

  // Helper function to extract and render download link using fileRegex, fileRegexone, fileRegextwo
  function renderDownloadLinkWithRegex(msg: SageMessage) {
    const downloadLinkData = getDownloadLinkData(msg);
    if (!downloadLinkData) return null;
    const { fileName, filePath, vendorName } = downloadLinkData;
    if (!filePath || !fileName) return null;
    return (
      <span>
        <div style={{ marginBottom: '8px' }}>
          Your Excel export is ready. You can download the vendor programs
          {vendorName ? ` for ${vendorName}` : ''} from the following link:
        </div>
        <a
          href={filePath}
          onClick={(e) => handleDownloadVendorPrograms(e, fileName)}
          style={{
            color: '#1976d2',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          {fileName}
        </a>
        <Box>
          <Snackbar
            autoHideDuration={4000}
            message="Download failed"
            onClose={() => setSnackbarOpen(false)}
            open={snackbarOpen}
          />
        </Box>
      </span>
    );
  }
  const [userInput, setUserInput] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingThumbsup, setLoadingThumbsup] = useState(false);
  const [loadingThumbsdown, setLoadingThumbsdown] = useState(false);
  const postSageSse = useSageSsePost();
  // State to hold the dynamic file_id
  const [fileId, setFileId] = useState<string | null>(null);
  const downloadUrl = useVenderProgramsDownloadFile({
    file_id: fileId || '',
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleDownloadVendorPrograms = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    fileName: string
  ) => {
    e.preventDefault();
    downloadUrl.mutate(undefined, {
      onError: () => {
        setSnackbarOpen(true);
      },
      onSuccess: (response) => {
        downloadBlob(response, fileName);
      },
    });
  };

  const handleSend = (value?: string) => {
    if (loading) return;
    if (!userInput.trim() && !value) return;
    if (value) {
      setUserInput('');
      if (value === 'Agent was able to solve the issue') {
        setLoadingThumbsup(true);
      } else if (value === 'Agent was NOT able to solve the issue') {
        setLoadingThumbsdown(true);
      }
      postAPI(value);
    } else {
      const userText = userInput.trim();
      const newMessage: SageMessage = {
        isApiResponse: false,
        sender: 'user',
        text: userText,
      };
      setMessages((prev): SageMessage[] => [...prev, newMessage]);
      setMessages((prev) => [
        ...prev,
        { isApiResponse: false, sender: 'bot', text: 'Thinking...' },
      ]);
      setUserInput('');
      setLoading(true);
      postAPI(userText);
    }
  };
  // Main API handler
  const postAPI = async (query: string) => {
    try {
      const updateStreamingBubble = createStreamingBubbleUpdater(setMessages);
      const updateThinkingMessage = (text: string) =>
        updateStreamingBubble(`Thinking: ${text}`, false);
      const { getStreamedResponse, updateFinalResponse } =
        createFinalResponseUpdater(updateStreamingBubble);

      const response = await postSageSse(query);

      if (!response.ok || !response.body) {
        throw new Error(`API failed with status: ${response.status}`);
      }

      const contentType =
        response.headers.get('content-type')?.toLowerCase() ?? '';
      if (!contentType.includes('text/event-stream')) {
        throw new Error('SSE response expected but not returned by server');
      }

      await consumeSseStream(response.body, {
        updateFinalResponse,
        updateThinkingMessage,
      });

      const streamedResponse = getStreamedResponse();

      if (streamedResponse.trim()) {
        updateStreamingBubble(streamedResponse, true);
      } else {
        updateStreamingBubble('Response received, but no content found.', true);
      }
      const fileIdFromResponse = extractFileId({ response: streamedResponse });
      if (fileIdFromResponse) setFileId(fileIdFromResponse);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          isApiResponse: false,
          sender: 'bot',
          text: 'Oops! Something went wrong. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
      setLoadingThumbsup(false);
      setLoadingThumbsdown(false);
    }
  };

  const toggleChat = () => setOpen((prev) => !prev);

  const handleFabClick = () => {
    if (!dragRef.current.moved) {
      toggleChat();
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !loading) {
      handleSend();
    }
  };
  useEffect(() => {
    if (location.pathname !== '/') setOpen(false);
  }, [location]);

  const target = document.getElementById('sage-root');
  if (!target) return null;

  return createPortal(
    <>
      <Fab
        color="primary"
        onClick={handleFabClick}
        onMouseDown={handleFabMouseDown}
        sx={{
          '&:active': {
            cursor: 'grabbing',
          },
          bottom: 'auto',
          boxShadow: 4,
          cursor: 'grab',
          left: `${buttonPos.x}px`,
          position: 'fixed',
          right: 'auto',
          top: `${buttonPos.y}px`,
          zIndex: 2001,
        }}
      >
        <AssistantIcon />
      </Fab>
      {open && (
        <Paper
          elevation={6}
          sx={{
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 12,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            bottom: 1,
            boxShadow: 6,
            display: 'flex',
            flexDirection: 'column',
            height: '90vh',
            left: 'auto',
            margin: 0,
            overflow: 'hidden',
            position: 'fixed',
            right: 1,
            width: '50vw',
            zIndex: 2001,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              p: 2,
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <PsychologyIcon />
              <Typography variant="h6">SAGE Agent</Typography>
            </Box>
            <IconButton
              onClick={toggleChat}
              size="small"
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {/* Message List */}
          <Box sx={{ bgcolor: '#fafafa', flex: 1, overflowY: 'auto', p: 2 }}>
            <List>
              {messages.map((msg, index) => (
                <ListItem
                  key={index}
                  sx={{
                    justifyContent:
                      msg.sender === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  {msg.sender === 'bot' && (
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                      <PsychologyIcon fontSize="small" />
                    </Avatar>
                  )}
                  <Paper
                    sx={{
                      bgcolor:
                        msg.sender === 'user' ? 'primary.main' : 'grey.200',
                      borderRadius: 2,
                      color: msg.sender === 'user' ? 'white' : 'black',
                      maxWidth: '80%',
                      p: 1.5,
                      wordBreak:
                        msg.sender === 'user' ? 'normal' : 'break-word',
                    }}
                  >
                    <ListItemText>
                      {/* Show message before the .xlsx link */}
                      {renderDownloadLinkWithRegex(msg) || (
                        <Markdown remarkPlugins={[remarkBreaks]}>
                          {toSafeMarkdownText(msg.text)}
                        </Markdown>
                      )}
                    </ListItemText>
                    {/* Thumbs up/down for bot responses from API only */}
                    {msg.sender === 'bot' && msg.isApiResponse && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <IconButton
                          aria-label="Thumbs Up"
                          disabled={loadingThumbsup}
                          onClick={() =>
                            handleSend(translate('VendorProgram.emojiThumbsup'))
                          }
                          size="small"
                          sx={{ color: 'success.main' }}
                        >
                          <ThumbUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          aria-label="Thumbs Down"
                          disabled={loadingThumbsdown}
                          onClick={() =>
                            handleSend(
                              translate('VendorProgram.emojiThumbsdown')
                            )
                          }
                          size="small"
                          sx={{ color: 'error.main' }}
                        >
                          <ThumbDownIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                </ListItem>
              ))}
            </List>
          </Box>
          {/* Input Field */}
          <Box
            sx={{
              bgcolor: 'white',
              borderTop: '1px solid #ddd',
              display: 'flex',
              gap: 1,
              p: 2,
              pb: 0.5,
            }}
          >
            <TextField
              InputProps={{
                style: {
                  fontSize: '20px',
                  height: '64px',
                  minHeight: '48px',
                  padding: '12px',
                },
              }}
              disabled={loading}
              fullWidth
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type your message..."
              size="small"
              sx={{ fontSize: 20, width: '99%' }}
              value={userInput}
            />
            <IconButton
              color="primary"
              disabled={loading}
              onClick={() => handleSend()}
            >
              {loading ? (
                <CircularProgress color="inherit" size={22} />
              ) : (
                <SendIcon />
              )}
            </IconButton>
          </Box>
          <SageAiDisclaimer />
        </Paper>
      )}
    </>,
    target
  );
}
